import React, { useCallback, useState } from 'react';
import { getStripe } from '../../../../shared/checkout/stripeClient';
import {
	useStripe,
	useElements,
	Elements,
	PaymentElement,
} from '@stripe/react-stripe-js';
import { useDispatch, useSelector } from 'react-redux';
import { CreditCard } from '@phosphor-icons/react';
import { RootState } from '../../../../redux/store';
import {
	getCreateIntent,
	sendStripeToken,
} from '../../../../services/sales.service';
import { AlertPayload, showAlert } from '../../../../redux/slices/alert.slice';
import ROUTES from '../../../../shared/constants/routes';
import {
	EVENT_TYPES,
} from '../../../../shared/constants/events';
import Button from '../../../../components/atoms/Button';
import useEvents from '../../../../hooks/useEvents';

const stripePromise = getStripe();
interface CreateCardProps {
	onPaymentMethodSuccess?: () => void;
}
const CreateCard: React.FC<CreateCardProps> = ({
	onPaymentMethodSuccess,
}) => {
	return (
		<>
			<Elements
				stripe={stripePromise}
				options={{
					mode: 'setup',
					currency: 'mxn',
				}}
			>
				<WidgetForm
					onPaymentMethodSuccess={onPaymentMethodSuccess}
				/>
			</Elements>
		</>
	);
};

const WidgetForm: React.FC<CreateCardProps> = ({ onPaymentMethodSuccess }) => {
	const [isLoadingPaymentMethod, setIsLoadingPaymentMethod] = useState(false);

	const { tokenSession } = useSelector((state: RootState) => state.auth);

	const stripe = useStripe();
	const elements = useElements();
	const dispatch = useDispatch();
	const { trackEvent: trackCheckoutEvent } = useEvents();
	const { user: userProfile } = useSelector((state: RootState) => state.profile);

	const get_ClientIntent = useCallback(async (userId: number) => {
		try {
			const response = await getCreateIntent(userId, tokenSession);
			return response;
		} catch (error) {}
	}, []);

	const showAlertTop = ({ title, type, description }: AlertPayload) => {
		const alert = {
			type,
			title,
			description,
		};
		dispatch(showAlert(alert));
	};

	const handlerSaveCard = async (event: React.FormEvent<HTMLFormElement>) => {
		try {
			event.preventDefault();

			if (!stripe) return;

			setIsLoadingPaymentMethod(true);

			const { error: submitError } = await elements!.submit();

			if (submitError) {
				showAlertTop({
					title: 'Error',
					type: 'error',
					description:
						submitError.message ??
						'Ocurrió un error al guardar la tarjeta, intenta de nuevo',
				});
				setIsLoadingPaymentMethod(false);
				return;
			}

			const res = await get_ClientIntent(userProfile?.id!);
			const clientSecret = res?.Body?.responseCall?.data?.client_secret;

			if (!clientSecret) {
				showAlertTop({
					title: 'Error',
					type: 'error',
					description:
						'Ocurrió un error al intentar guardar la tarjeta, intenta de nuevo',
				});
			}

			const { error, setupIntent } = await stripe!.confirmSetup({
				elements: elements!,
				redirect: 'if_required',
				clientSecret: clientSecret!,
				confirmParams: {
					return_url: location.href + ROUTES.CHECKOUT,
				},
			});
			setIsLoadingPaymentMethod(false);

			if (error) {
				trackCheckoutEvent({
					eventName: EVENT_TYPES.CHECKOUT_STEP_INTERACTED,
					eventPayload: {
						step_name: 'card_management',
						action_type: 'failure',
						user_id: userProfile?.id,
						error_type: error.type || 'stripe_error',
						error_code: error.code || 'unknown',
						error_message: error.message || 'Failed to add payment card',
					},
				});

				showAlertTop({
					title: 'Error',
					type: 'error',
					description: error.message ?? '',
				});
			} else {
				await sendStripeToken(
					(setupIntent.payment_method as string) ?? '',
					tokenSession,
				);

				trackCheckoutEvent({
					eventName: EVENT_TYPES.CHECKOUT_STEP_INTERACTED,
					eventPayload: {
						step_name: 'card_management',
						action_type: 'added',
						user_id: userProfile?.id,
						payment_method: 'CARD',
					},
				});
				showAlertTop({
					title: 'Tarjeta guardada',
					type: 'success',
					description: 'Tarjeta guardada exitosamente',
				});
				onPaymentMethodSuccess?.();
			}
		} catch (error) {
			setIsLoadingPaymentMethod(false);
			showAlertTop({
				title: 'Error',
				type: 'error',
				description: 'Ocurrió un error al guardar la tarjeta, intenta de nuevo',
			});
		}
	};

	return (
		<form onSubmit={handlerSaveCard}>
			<PaymentElement />
			<Button
				beforeIcon={<CreditCard size={24} />}
				loading={isLoadingPaymentMethod}
				disabled={!stripe}
				variant='secondary'
				label='Agregar'
				sx='h-[47px] ml-2 mt-4'
				type='submit'
			/>
		</form>
	);
};

export default CreateCard;
